import { useCallback, useEffect, useMemo, useState } from "react";

const fmtDateTime = (iso) => (iso ? new Date(iso).toLocaleString("en-GB", { day: "numeric", month: "short", hour: "2-digit", minute: "2-digit" }) : "—");
const TRACK_KIND = {
  invoice: ["Invoice", "bg-sky-50 text-[#005EB8]"],
  quote: ["Quote", "bg-violet-50 text-violet-700"],
  proposal: ["Proposal", "bg-teal-50 text-teal-700"],
  reminder: ["Reminder", "bg-amber-50 text-[#ED8B00]"],
};
import { useNavigate, useSearchParams, Link } from "react-router-dom";
import { ChevronLeft, ChevronDown, ChevronRight, Plus, Send, BellRing, Check, Pencil, Trash2, Printer, Archive, ArchiveRestore, Undo2, FileCheck2, XCircle, Landmark, PoundSterling, CalendarDays, Upload, Download } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import AdminHeader from "@/components/AdminHeader";

const inputCls = "w-full h-12 rounded-lg border border-slate-300 px-3 text-sm focus:outline-none focus:ring-2 focus:ring-[#005EB8]";
const API_BASE = process.env.REACT_APP_BACKEND_URL;

const EMPTY_ITEM = { description: "", qty: 1, unit_price: "" };
let itemKeySeq = 0;
const newItem = (over = {}) => ({ ...EMPTY_ITEM, ...over, _k: `item-${++itemKeySeq}` });
const EMPTY_FORM = { kind: "invoice", client_id: "", contact_id: "", event_id: "", event_ids: [], due_date: "", notes: "", items: [newItem()], bank_account_id: "", discount_on: false, discount_type: "percent", discount_value: "" };

const fmtDate = (iso) => {
  if (!iso) return "";
  const d = new Date(iso.length <= 10 ? `${iso}T00:00:00` : iso);
  return d.toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" });
};

const QUOTE_STATUS = {
  draft: { label: "Draft", cls: "bg-slate-100 text-slate-600" },
  sent: { label: "Sent", cls: "bg-sky-50 text-[#005EB8]" },
  accepted: { label: "Accepted", cls: "bg-emerald-50 text-[#007F3B]" },
  declined: { label: "Declined", cls: "bg-red-50 text-[#D5281B]" },
};

export default function Invoices() {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [clients, setClients] = useState([]);
  const [events, setEvents] = useState([]);
  const [tab, setTab] = useState("outstanding");
  const [tracking, setTracking] = useState(null);
  const [dialog, setDialog] = useState(false);
  const [form, setForm] = useState(EMPTY_FORM);
  const [editing, setEditing] = useState(null);
  const [paidDialog, setPaidDialog] = useState(null);
  const [paidAt, setPaidAt] = useState("");
  const [chaseDialog, setChaseDialog] = useState(null);
  const [chaseMsg, setChaseMsg] = useState("");
  const [uploadOpen, setUploadOpen] = useState(false);
  const [uploadForm, setUploadForm] = useState({ client_id: "", client_name: "", invoice_no: "", event_name: "", total: "", due_date: "", status: "unpaid", notes: "" });
  const [uploadFile, setUploadFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [depositDialog, setDepositDialog] = useState(null);
  const [depositAmt, setDepositAmt] = useState("");
  const [depositNote, setDepositNote] = useState("");
  const [saving, setSaving] = useState(false);
  const [bankAccounts, setBankAccounts] = useState([]);
  const [bankDialog, setBankDialog] = useState(false);
  const [bankList, setBankList] = useState([]);

  const load = useCallback(async () => {
    try {
      const [{ data: inv }, { data: cl }, { data: ev }] = await Promise.all([
        api.get("/invoices"), api.get("/clients"), api.get("/events"),
      ]);
      setData(inv);
      if (inv.bank_accounts) setBankAccounts(inv.bank_accounts);
      setClients(cl);
      setEvents(ev);
    } catch (err) {
      toast.error(formatApiError(err));
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const [searchParams, setSearchParams] = useSearchParams();

  const clientParam = searchParams.get("client") || "";
  const summary = data?.summary;
  const [monthFilter, setMonthFilter] = useState("");
  const [summaryHidden, setSummaryHidden] = useState(() => localStorage.getItem("wems-inv-hide-summary") === "1");

  const docMonthKey = (inv) => {
    const iso = (inv.status === "paid" && inv.paid_at) ? inv.paid_at : (inv.created_at || inv.due_date || "");
    return iso ? String(iso).slice(0, 7) : "";
  };
  const monthLabel = (k) => {
    const [y, m] = k.split("-");
    return new Date(Number(y), Number(m) - 1, 1).toLocaleDateString("en-GB", { month: "long", year: "numeric" });
  };
  const monthOptions = useMemo(() => {
    const keys = new Set();
    (data?.invoices || []).forEach((i) => { const k = docMonthKey(i); if (k) keys.add(k); });
    return [...keys].sort().reverse();
  }, [data]);
  const quotesPotential = useMemo(() => (data?.invoices || [])
    .filter((i) => i.kind === "quote" && i.status === "sent" && !i.archived)
    .reduce((s, i) => s + (i.total || 0), 0), [data]);

  const visible = useMemo(() => (data?.invoices || []).filter((i) => {
    if (clientParam && i.client_id !== clientParam) return false;
    if (monthFilter && docMonthKey(i) !== monthFilter) return false;
    const isQuote = i.kind === "quote";
    if (tab === "outstanding") return !isQuote && i.status !== "paid" && !i.archived;
    if (tab === "paid") return !isQuote && i.status === "paid";
    if (tab === "quotes") return isQuote && !i.archived;
    if (tab === "archived") return i.archived;
    return true;
  }), [data, tab, clientParam, monthFilter]);

  const grouped = useMemo(() => {
    const todayStr = new Date().toISOString().slice(0, 10);
    const groups = {};
    for (const inv of visible) {
      const key = inv.client_id || inv.client_name || "unknown";
      (groups[key] = groups[key] || { key, name: inv.client_name || "No client", items: [] }).items.push(inv);
    }
    return Object.values(groups).map((g) => ({
      ...g,
      total: g.items.reduce((s, i) => s + Math.max((i.total || 0) - (i.deposit_total || 0), 0), 0),
      overdue: g.items.some((i) => i.kind !== "quote" && i.status !== "paid" && i.status !== "draft" && i.due_date && i.due_date < todayStr),
    })).sort((a, b) => a.name.localeCompare(b.name));
  }, [visible]);

  const loadTracking = useCallback(async () => {
    setTracking(null);
    try {
      const { data: t } = await api.get("/invoices/tracking/list");
      setTracking(t);
    } catch (err) {
      toast.error(formatApiError(err));
      setTracking([]);
    }
  }, []);

  useEffect(() => { if (tab === "tracking") loadTracking(); }, [tab, loadTracking]);

  const openCreate = useCallback((kind) => { setEditing(null); setForm({ ...EMPTY_FORM, kind, items: [newItem()] }); setDialog(true); }, []);
  const openEdit = (inv) => {
    setEditing(inv);
    setForm({
      kind: inv.kind || "invoice", client_id: inv.client_id, contact_id: inv.contact_id,
      event_id: inv.event_id || "", event_ids: inv.event_ids || (inv.event_id ? [inv.event_id] : []), due_date: inv.due_date, notes: inv.notes,
      items: inv.items.map((i) => newItem({ description: i.description, qty: i.qty, unit_price: i.unit_price })),
      bank_account_id: inv.bank_account_id || "",
      discount_on: (inv.discount_amount || 0) > 0, discount_type: inv.discount_type || "percent",
      discount_value: inv.discount_value || "",
    });
    setDialog(true);
  };

  const pickClient = (clientId) => {
    const cl = clients.find((c) => c.id === clientId);
    setForm((f) => ({
      ...f, client_id: clientId, contact_id: cl?.contacts?.[0]?.contact_id || "",
      bank_account_id: cl?.bank_account_id || "",
      items: cl?.default_price != null
        ? f.items.map((it, i) => (i === 0 && it.unit_price === "") ? { ...it, unit_price: String(cl.default_price) } : it)
        : f.items,
    }));
  };

  const toggleEvent = useCallback((eventId) => {
    const ev = events.find((e) => e.id === eventId);
    const cl = clients.find((c) => c.id === form.client_id);
    setForm((f) => {
      const has = f.event_ids.includes(eventId);
      if (has) {
        const items = f.items.filter((it) => it.event_id !== eventId);
        const event_ids = f.event_ids.filter((id) => id !== eventId);
        return { ...f, event_ids, event_id: event_ids[0] || "", items: items.length ? items : [newItem()] };
      }
      const price = cl?.default_price != null ? String(cl.default_price) : (ev?.finances?.client_charge ? String(ev.finances.client_charge) : "");
      const line = newItem({
        description: `Event medical cover — ${ev?.name || ""}${ev?.event_date ? ` (${ev.event_date})` : ""}`,
        qty: 1, unit_price: price, event_id: eventId,
      });
      const items = f.items.length === 1 && !f.items[0].description ? [line] : [...f.items, line];
      const event_ids = [...f.event_ids, eventId];
      return { ...f, event_ids, event_id: event_ids[0] || "", items };
    });
  }, [events, clients, form.client_id]);

  useEffect(() => {
    const ev = searchParams.get("event");
    if (ev && events.length) {
      openCreate(searchParams.get("kind") === "quote" ? "quote" : "invoice");
      toggleEvent(ev);
      setSearchParams({}, { replace: true });
      return;
    }
    const isNew = searchParams.get("new");
    if (isNew && clients.length) {
      openCreate(isNew === "quote" ? "quote" : "invoice");
      const cid = searchParams.get("client");
      if (cid) pickClient(cid);
      setSearchParams({}, { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams, events, clients, openCreate, toggleEvent, setSearchParams]);

  const setItem = (idx, k, v) => setForm((f) => {
    const items = [...f.items];
    items[idx] = { ...items[idx], [k]: v };
    return { ...f, items };
  });

  const subtotal = form.items.reduce((acc, i) => acc + (Number(i.qty) || 0) * (Number(i.unit_price) || 0), 0);
  const discountAmt = form.discount_on
    ? Math.min(form.discount_type === "percent" ? subtotal * ((Number(form.discount_value) || 0) / 100) : (Number(form.discount_value) || 0), subtotal)
    : 0;
  const total = subtotal - discountAmt;

  const save = async (e) => {
    e.preventDefault();
    const items = form.items
      .filter((i) => i.description.trim())
      .map((i) => ({ description: i.description.trim(), qty: Number(i.qty) || 1, unit_price: Number(i.unit_price) || 0 }));
    if (!items.length) { toast.error("Add at least one line"); return; }
    if (!editing && form.event_ids.length === 0) { toast.error("Pick at least one calendar event this is for"); return; }
    if (!form.due_date) { toast.error("Pick a payment due date"); return; }
    setSaving(true);
    try {
      if (editing) {
        await api.patch(`/invoices/${editing.id}`, { client_id: form.client_id, contact_id: form.contact_id, due_date: form.due_date, notes: form.notes, items, bank_account_id: form.bank_account_id, discount_type: form.discount_on ? form.discount_type : "", discount_value: form.discount_on ? Number(form.discount_value) || 0 : 0 });
        toast.success("Saved");
      } else {
        const allocations = items.filter((i) => i.event_id).map((i) => ({ event_id: i.event_id, amount: i.qty * i.unit_price }));
        const { data } = await api.post("/invoices", { ...form, items, allocations, discount_type: form.discount_on ? form.discount_type : "", discount_value: form.discount_on ? Number(form.discount_value) || 0 : 0 });
        toast.success(`${form.kind === "quote" ? "Quote" : "Invoice"} ${data.invoice_no} created`);
      }
      setDialog(false);
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSaving(false);
    }
  };

  const act = async (fn, success) => {
    try {
      const r = await fn();
      toast.success(success || r.data?.message || "Done");
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const saveBanks = async () => {
    setSaving(true);
    try {
      const { data } = await api.put("/bank-accounts", { accounts: bankList });
      setBankAccounts(data.accounts);
      setBankDialog(false);
      toast.success("Bank accounts saved");
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSaving(false);
    }
  };

  const [sendDialog, setSendDialog] = useState(null);
  const [sendPick, setSendPick] = useState({ contact_ids: [], extra: "" });

  const openSend = (inv) => {
    setSendDialog(inv);
    setSendPick({ contact_ids: [], extra: "" });
  };

  const confirmSend = async () => {
    const extras = sendPick.extra.split(/[,\s;]+/).map((e) => e.trim()).filter((e) => e.includes("@"));
    setSaving(true);
    try {
      const { data } = await api.post(`/invoices/${sendDialog.id}/send`, { contact_ids: sendPick.contact_ids, extra_emails: extras });
      toast.success(data.message);
      setSendDialog(null);
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSaving(false);
    }
  };
  const chase = (inv) => { setChaseDialog(inv); setChaseMsg(""); };
  const uploadInvoice = async () => {
    if (!uploadFile) { toast.error("Choose the invoice file first"); return; }
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", uploadFile);
      for (const [k, v] of Object.entries(uploadForm)) fd.append(k, v);
      await api.post("/invoices/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Invoice uploaded — it's in the list");
      setUploadOpen(false);
      setUploadForm({ client_id: "", client_name: "", invoice_no: "", event_name: "", total: "", due_date: "", status: "unpaid", notes: "" });
      setUploadFile(null);
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setUploading(false);
    }
  };
  const downloadUploaded = async (inv) => {
    try {
      const res = await api.get(`/invoices/${inv.id}/file`, { responseType: "blob" });
      const url = URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement("a");
      a.href = url; a.download = inv.uploaded_file?.name || `${inv.invoice_no}.pdf`; a.click();
      URL.revokeObjectURL(url);
    } catch (err) { toast.error(formatApiError(err)); }
  };
  const sendChase = (mode) => act(async () => {
    await api.post(`/invoices/${chaseDialog.id}/chase`, { mode, message: chaseMsg });
    setChaseDialog(null);
  });
  const acceptQuote = (inv) => {
    if (!window.confirm(`Mark quote ${inv.invoice_no} as accepted? An invoice will be generated automatically.`)) return;
    act(() => api.post(`/invoices/${inv.id}/accept`));
  };
  const declineQuote = (inv) => act(() => api.post(`/invoices/${inv.id}/decline`));
  const markUnpaid = (inv) => act(() => api.post(`/invoices/${inv.id}/mark-unpaid`));
  const toggleArchive = (inv) => act(() => api.patch(`/invoices/${inv.id}`, { archived: !inv.archived }));
  const remove = (inv) => {
    if (!window.confirm(`Delete ${inv.invoice_no} for ${inv.client_name}? This can't be undone.`)) return;
    act(() => api.delete(`/invoices/${inv.id}`));
  };
  const confirmPaid = () => act(async () => {
    await api.post(`/invoices/${paidDialog.id}/mark-paid`, { paid_at: paidAt });
    setPaidDialog(null);
  });
  const confirmDeposit = () => act(async () => {
    await api.post(`/invoices/${depositDialog.id}/deposit`, { amount: Number(depositAmt) || 0, note: depositNote });
    setDepositDialog(null);
  });

  const selectedClient = clients.find((c) => c.id === form.client_id);
  const sortedEvents = [...events].filter((e) => e.event_date).sort((a, b) => a.event_date.localeCompare(b.event_date));
  const todayISO = new Date().toLocaleDateString("en-CA");
  const invoicedEventIds = new Set((data?.invoices || []).filter((i) => i.kind !== "quote" && !i.archived).flatMap((i) => (i.event_ids?.length ? i.event_ids : [i.event_id]).filter(Boolean)));
  const [eventSearch, setEventSearch] = useState("");
  const [openGroups, setOpenGroups] = useState({});
  const [eventPickerOpen, setEventPickerOpen] = useState(false);
  const currentYear = String(new Date().getFullYear());
  const pickableEvents = sortedEvents.filter((ev) => {
    const last = ev.end_date && ev.end_date > ev.event_date ? ev.end_date : ev.event_date;
    if (last < todayISO) return false;
    if (form.kind !== "quote" && invoicedEventIds.has(ev.id)) return false;
    if (!eventSearch.trim() && !ev.event_date.startsWith(currentYear)) return false;
    return true;
  });
  const eventMatches = pickableEvents
    .filter((ev) => !form.client_id || ev.client_id === form.client_id)
    .filter((ev) => !form.event_ids.includes(ev.id))
    .filter((ev) => ev.name.toLowerCase().includes(eventSearch.trim().toLowerCase()))
    .slice(0, 8);

  return (
    <div className="min-h-screen bg-[#F4F7FA]" data-testid="invoices-page">
      <AdminHeader title="Quotes & invoices" testid="invoices-title" backTestid="invoices-back"
        actions={(
          <>
            <button onClick={() => { setBankList(bankAccounts.map((a) => ({ ...a }))); setBankDialog(true); }} data-testid="bank-accounts-btn"
              className="h-11 px-4 rounded-lg border border-slate-300 text-slate-600 hover:border-[#005EB8] hover:text-[#005EB8] transition-colors text-sm font-bold inline-flex items-center gap-1.5 bg-white press">
              <Landmark className="h-4 w-4" /> Bank accounts
            </button>
            <button onClick={() => setUploadOpen(true)} data-testid="upload-invoice-btn"
              className="h-11 px-4 rounded-lg border border-slate-300 text-slate-600 hover:border-[#005EB8] hover:text-[#005EB8] transition-colors text-sm font-bold inline-flex items-center gap-1.5 bg-white press">
              <Upload className="h-4 w-4" /> Upload invoice
            </button>
            <button onClick={() => openCreate("quote")} data-testid="new-quote-btn"
              className="h-11 px-4 rounded-lg border-2 border-[#005EB8] text-[#005EB8] hover:bg-sky-50 transition-colors text-sm font-bold inline-flex items-center gap-1.5 bg-white press">
              <Plus className="h-4 w-4" /> New quote
            </button>
            <button onClick={() => openCreate("invoice")} data-testid="new-invoice-btn"
              className="h-11 px-4 rounded-lg bg-[#005EB8] hover:bg-[#004a93] transition-colors text-white text-sm font-bold inline-flex items-center gap-1.5 press">
              <Plus className="h-4 w-4" /> New invoice
            </button>
          </>
        )} />

      <main className="max-w-5xl mx-auto p-4 sm:p-6">
        {summary && summaryHidden && (
          <button onClick={() => { setSummaryHidden(false); localStorage.setItem("wems-inv-hide-summary", "0"); }} data-testid="summary-show"
            className="mb-4 h-9 px-3 rounded-lg border border-dashed border-slate-300 text-xs font-bold text-slate-400 hover:text-[#005EB8] hover:border-[#005EB8] transition-colors">
            Show summary tiles
          </button>
        )}
        {summary && !summaryHidden && (
          <div className="relative">
            <button onClick={() => { setSummaryHidden(true); localStorage.setItem("wems-inv-hide-summary", "1"); }} data-testid="summary-hide" title="Hide these tiles"
              className="absolute -top-2 right-0 h-6 px-2.5 rounded-full bg-white border border-slate-200 text-[10px] font-bold text-slate-400 hover:text-slate-600 hover:border-slate-300 z-10 transition-colors">
              Hide
            </button>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-5">
            <div className="bg-white rounded-xl border-2 border-[#ED8B00]/50 p-4" data-testid="summary-outstanding">
              <p className="text-2xl font-black text-[#ED8B00]">£{summary.outstanding_total.toFixed(2)}</p>
              <p className="text-xs text-slate-500 font-semibold mt-0.5">Outstanding · {summary.outstanding_count}</p>
            </div>
            <div className="bg-white rounded-xl border border-slate-200 p-4" data-testid="summary-paid">
              <p className="text-2xl font-black text-[#007F3B]">£{(summary.paid_month_total ?? 0).toFixed(2)}</p>
              <p className="text-xs text-slate-500 font-semibold mt-0.5">Paid in {new Date().toLocaleDateString("en-GB", { month: "long" })} · {summary.paid_month_count ?? 0} invoice{(summary.paid_month_count ?? 0) === 1 ? "" : "s"}</p>
            </div>
            <div className="bg-white rounded-xl border border-slate-200 p-4" data-testid="summary-quotes">
              <p className="text-2xl font-black text-[#005EB8]">{summary.quotes_pending}</p>
              <p className="text-xs text-slate-500 font-semibold mt-0.5">Quotes awaiting answer</p>
              <p className="text-xs font-bold text-[#005EB8] mt-0.5" data-testid="summary-quotes-potential">£{quotesPotential.toFixed(2)} potential</p>
            </div>
            <div className="bg-white rounded-xl border border-slate-200 p-4" data-testid="summary-by-client">
              <p className="text-xs text-slate-500 font-semibold mb-1">Who owes what</p>
              {summary.by_client.length === 0 ? (
                <p className="text-xs text-slate-400">Nothing outstanding</p>
              ) : summary.by_client.map((b) => (
                <p key={b.client} className="text-xs font-semibold text-[#0F2547] truncate">{b.client} — <span className="text-[#ED8B00]">£{b.outstanding.toFixed(2)}</span></p>
              ))}
            </div>
          </div>
          </div>
        )}

        <div className="flex gap-2 mb-4 flex-wrap items-center" data-testid="invoice-tabs">
          {[["outstanding", "Outstanding"], ["paid", "Paid"], ["quotes", "Quotes"], ["tracking", "Email tracking"], ["archived", "Archived"], ["all", "All"]].map(([k, label]) => (
            <button key={k} onClick={() => setTab(k)} data-testid={`tab-${k}`}
              className={`h-10 px-4 rounded-lg text-xs font-bold transition-colors ${tab === k ? "bg-[#0F2547] text-white" : "bg-white border border-slate-300 text-slate-600 hover:border-[#005EB8]"}`}>
              {label}
            </button>
          ))}
          {tab !== "tracking" && monthOptions.length > 0 && (
            <select value={monthFilter} onChange={(e) => setMonthFilter(e.target.value)} data-testid="month-filter"
              className="h-10 px-3 rounded-lg border border-slate-300 bg-white text-xs font-bold text-slate-600 ml-auto">
              <option value="">All months</option>
              {monthOptions.map((k) => <option key={k} value={k}>{monthLabel(k)}</option>)}
            </select>
          )}
        </div>

        {tab === "tracking" ? (
          tracking === null ? (
            <p className="text-sm text-slate-500">Loading…</p>
          ) : tracking.length === 0 ? (
            <div className="bg-white rounded-2xl border border-slate-200 p-10 text-center" data-testid="tracking-empty">
              <p className="text-sm text-slate-500">No invoice or quote emails sent yet — they'll appear here once sent.</p>
            </div>
          ) : (
            <div className="space-y-2" data-testid="tracking-list">
              <div className="flex items-center justify-between gap-2">
                <p className="text-[11px] text-slate-400">"Opened" registers when the recipient's email app loads images — some apps (e.g. Apple Mail) load them automatically, so treat it as a guide, not proof.</p>
              </div>
              {tracking.map((t) => {
                const [kLabel, kCls] = TRACK_KIND[t.kind] || TRACK_KIND.invoice;
                return (
                  <div key={t.id} className="bg-white rounded-xl border border-slate-200 px-4 py-3 flex items-center gap-3 flex-wrap" data-testid={`tracking-row-${t.id}`}>
                    <span className={`text-[10px] font-bold uppercase tracking-wider rounded-full px-2 py-0.5 shrink-0 ${kCls}`}>{kLabel}</span>
                    <div className="min-w-0 flex-1">
                      <p className="text-sm font-bold text-[#0F2547]">{t.invoice_no} · {t.client_name}</p>
                      <p className="text-xs text-slate-500 truncate">To {t.to} · sent {fmtDateTime(t.sent_at)}{t.sent_by ? ` by ${t.sent_by}` : ""}</p>
                    </div>
                    {t.opens > 0 ? (
                      <span className="shrink-0 text-[11px] font-bold text-[#007F3B] bg-emerald-50 border border-emerald-200 rounded-full px-3 py-1" data-testid={`tracking-status-${t.id}`}>
                        Opened{t.opens > 1 ? ` ${t.opens}×` : ""} · first {fmtDateTime(t.first_opened_at)}
                      </span>
                    ) : (
                      <span className="shrink-0 text-[11px] font-bold text-slate-400 bg-slate-50 border border-slate-200 rounded-full px-3 py-1" data-testid={`tracking-status-${t.id}`}>
                        Sent — not opened yet
                      </span>
                    )}
                    {t.clicks > 0 && (
                      <span className="shrink-0 text-[11px] font-bold text-[#005EB8] bg-blue-50 border border-blue-200 rounded-full px-3 py-1" data-testid={`tracking-clicks-${t.id}`}>
                        Link clicked{t.clicks > 1 ? ` ${t.clicks}×` : ""} · {fmtDateTime(t.last_clicked_at)}
                      </span>
                    )}
                  </div>
                );
              })}
            </div>
          )
        ) : data === null ? (
          <p className="text-sm text-slate-500">Loading…</p>
        ) : visible.length === 0 ? (
          <div className="bg-white rounded-2xl border border-slate-200 p-10 text-center" data-testid="invoices-empty">
            <p className="text-sm text-slate-500">Nothing here.</p>
          </div>
        ) : (
          <div className="space-y-5">
            {grouped.map((g) => (
              <div key={g.key}>
                <button onClick={() => setOpenGroups((o) => ({ ...o, [g.key]: !o[g.key] }))}
                  data-testid={`invoice-group-toggle-${g.key}`}
                  className="w-full flex items-center gap-2 px-1 mb-2 text-left group">
                  {openGroups[g.key] ? <ChevronDown className={`h-4 w-4 shrink-0 ${g.overdue ? "text-[#D5281B]" : "text-slate-400"}`} /> : <ChevronRight className={`h-4 w-4 shrink-0 ${g.overdue ? "text-[#D5281B]" : "text-slate-400"}`} />}
                  <span className={`text-xs font-bold uppercase tracking-wider transition-colors ${g.overdue ? "text-[#D5281B]" : "text-slate-500 group-hover:text-[#005EB8]"}`} data-testid={`invoice-group-${g.key}`}>
                    {g.name} · {g.items.length} {g.items.length === 1 ? "document" : "documents"} · £{g.total.toFixed(2)}{g.overdue ? " — OVERDUE" : ""}
                  </span>
                </button>
                {openGroups[g.key] && (
                <div className="space-y-3">
            {g.items.map((inv) => {
              const isQuote = inv.kind === "quote";
              return (
                <div key={inv.id} className={`card-lift bg-white rounded-2xl border p-4 sm:p-5 ${!isQuote && inv.status === "paid" ? "border-emerald-200" : "border-slate-200"}`} data-testid={`invoice-card-${inv.id}`}>
                  <div className="flex items-start justify-between gap-3 flex-wrap">
                    <div className="min-w-0">
                      <p className="font-heading text-base font-bold text-[#0F2547]">
                        {inv.invoice_no} ·{" "}
                        <Link to={`/admin/contacts?client=${inv.client_id}`} data-testid={`invoice-client-link-${inv.id}`}
                          className="link-chip">{inv.client_name}</Link>
                      </p>
                      <p className="text-xs text-slate-500 mt-0.5">
                        {inv.contact_name ? `${inv.contact_name}${inv.contact_email ? ` <${inv.contact_email}>` : ""}` : inv.contact_email || "No email on file"}
                      </p>
                      {inv.event_name && (
                        <Link to="/calendar" data-testid={`invoice-event-link-${inv.id}`} className="link-chip text-xs mt-0.5">
                          <CalendarDays className="h-3 w-3" /> {inv.event_name}{inv.event_date ? ` (${fmtDate(inv.event_date)})` : ""}
                        </Link>
                      )}
                      <p className="text-xs text-slate-500 mt-0.5">
                        {isQuote ? `Due date if accepted: ${fmtDate(inv.due_date)}` : <>Due <span className="font-bold text-[#ED8B00]">{fmtDate(inv.due_date)}</span></>}
                        {inv.sent_at ? ` · sent ${fmtDate(inv.sent_at)}` : " · not sent yet"}
                        {inv.chases > 0 ? ` · chased ${inv.chases}×` : ""}
                        {!isQuote && inv.status === "paid" && inv.paid_at ? ` · paid ${fmtDate(inv.paid_at)}` : ""}
                      </p>
                      {!isQuote && (inv.deposit_total || 0) > 0 && (
                        <p className="text-xs mt-0.5" data-testid={`invoice-deposit-line-${inv.id}`}>
                          <span className="font-bold text-[#007F3B]">Deposit paid £{inv.deposit_total.toFixed(2)}</span>
                          <span className="text-slate-500"> · balance left £{(inv.balance_due ?? inv.total).toFixed(2)}</span>
                        </p>
                      )}
                    </div>
                    <div className="text-right shrink-0">
                      <p className="text-xl font-black text-[#0F2547]" data-testid={`invoice-total-${inv.id}`}>£{inv.total.toFixed(2)}</p>
                      {isQuote ? (
                        <span className={`inline-block text-[10px] font-bold uppercase tracking-wider rounded-full px-2 py-0.5 mt-1 ${QUOTE_STATUS[inv.status]?.cls || QUOTE_STATUS.draft.cls}`} data-testid={`invoice-status-${inv.id}`}>
                          {QUOTE_STATUS[inv.status]?.label || inv.status}
                        </span>
                      ) : (
                        <span className={`inline-block text-[10px] font-bold uppercase tracking-wider rounded-full px-2 py-0.5 mt-1 ${inv.status === "paid" ? "bg-emerald-50 text-[#007F3B]" : "bg-amber-50 text-[#ED8B00]"}`} data-testid={`invoice-status-${inv.id}`}>
                          {inv.status === "paid" ? "Paid" : "Unpaid"}
                        </span>
                      )}
                    </div>
                  </div>
                  <div className="flex gap-2 mt-3 flex-wrap">
                    {(isQuote ? inv.status !== "accepted" && inv.status !== "declined" : inv.status !== "paid") && (
                      <button onClick={() => openSend(inv)} data-testid={`invoice-send-${inv.id}`}
                        className="h-10 px-3 rounded-lg bg-[#005EB8] hover:bg-[#004a93] transition-colors text-white text-xs font-bold inline-flex items-center gap-1.5">
                        <Send className="h-3.5 w-3.5" /> {inv.sent_at ? "Resend" : "Send"}
                      </button>
                    )}
                    {isQuote && inv.status !== "accepted" && inv.status !== "declined" && (
                      <>
                        <button onClick={() => acceptQuote(inv)} data-testid={`quote-accept-${inv.id}`}
                          className="h-10 px-3 rounded-lg bg-[#007F3B] hover:bg-[#006631] transition-colors text-white text-xs font-bold inline-flex items-center gap-1.5">
                          <FileCheck2 className="h-3.5 w-3.5" /> Accept → invoice
                        </button>
                        <button onClick={() => declineQuote(inv)} data-testid={`quote-decline-${inv.id}`}
                          className="h-10 px-3 rounded-lg border border-slate-300 text-slate-600 hover:border-[#D5281B] hover:text-[#D5281B] transition-colors text-xs font-bold inline-flex items-center gap-1.5 bg-white">
                          <XCircle className="h-3.5 w-3.5" /> Declined
                        </button>
                      </>
                    )}
                    {!isQuote && inv.status !== "paid" && (
                      <>
                        {inv.uploaded_file && (
                          <button onClick={() => downloadUploaded(inv)} data-testid={`invoice-download-${inv.id}`} title={`Download ${inv.uploaded_file.name}`}
                            className="h-9 px-3 rounded-lg border border-slate-200 text-[#007F3B] hover:bg-emerald-50 text-xs font-bold inline-flex items-center gap-1.5 transition-colors">
                            <Download className="h-3.5 w-3.5" /> File
                          </button>
                        )}
                        <button onClick={() => chase(inv)} data-testid={`invoice-chase-${inv.id}`}
                          className="h-10 px-3 rounded-lg bg-[#ED8B00] hover:bg-[#c97600] transition-colors text-white text-xs font-bold inline-flex items-center gap-1.5">
                          <BellRing className="h-3.5 w-3.5" /> Chase
                        </button>
                      </>
                    )}
                    {!isQuote && inv.status !== "paid" && (
                      <button onClick={() => { setDepositDialog(inv); setDepositAmt(""); setDepositNote(""); }} data-testid={`invoice-deposit-${inv.id}`}
                        className="h-10 px-3 rounded-lg border-2 border-[#007F3B] text-[#007F3B] hover:bg-emerald-50 transition-colors text-xs font-bold inline-flex items-center gap-1.5 bg-white">
                        <PoundSterling className="h-3.5 w-3.5" /> Deposit paid
                      </button>
                    )}
                    {!isQuote && (inv.status !== "paid" ? (
                      <button onClick={() => { setPaidDialog(inv); setPaidAt(new Date().toISOString().slice(0, 16)); }} data-testid={`invoice-mark-paid-${inv.id}`}
                        className="h-10 px-3 rounded-lg bg-[#007F3B] hover:bg-[#006631] transition-colors text-white text-xs font-bold inline-flex items-center gap-1.5">
                        <Check className="h-3.5 w-3.5" /> Mark paid
                      </button>
                    ) : (
                      <button onClick={() => markUnpaid(inv)} data-testid={`invoice-mark-unpaid-${inv.id}`}
                        className="h-10 px-3 rounded-lg border border-slate-300 text-slate-600 hover:border-[#ED8B00] hover:text-[#ED8B00] transition-colors text-xs font-bold inline-flex items-center gap-1.5 bg-white">
                        <Undo2 className="h-3.5 w-3.5" /> Mark unpaid
                      </button>
                    ))}
                    {!isQuote && inv.uploaded_file && inv.status === "paid" && (
                      <button onClick={() => downloadUploaded(inv)} data-testid={`invoice-download-${inv.id}`} title={`Download ${inv.uploaded_file.name}`}
                        className="h-9 px-3 rounded-lg border border-slate-200 text-[#007F3B] hover:bg-emerald-50 text-xs font-bold inline-flex items-center gap-1.5 transition-colors">
                        <Download className="h-3.5 w-3.5" /> File
                      </button>
                    )}
                    <a href={`${API_BASE}/api/public-invoice/${inv.print_token}`} target="_blank" rel="noreferrer" data-testid={`invoice-print-${inv.id}`}
                      className="h-10 px-3 rounded-lg border border-slate-300 text-slate-600 hover:border-[#005EB8] hover:text-[#005EB8] transition-colors text-xs font-bold inline-flex items-center gap-1.5 bg-white">
                      <Printer className="h-3.5 w-3.5" /> Print
                    </a>
                    {(isQuote ? inv.status === "draft" || inv.status === "sent" : inv.status !== "paid") && (
                      <button onClick={() => openEdit(inv)} data-testid={`invoice-edit-${inv.id}`}
                        className="h-10 px-3 rounded-lg border border-slate-300 text-slate-600 hover:border-[#005EB8] hover:text-[#005EB8] transition-colors text-xs font-bold inline-flex items-center gap-1.5 bg-white">
                        <Pencil className="h-3.5 w-3.5" /> Edit
                      </button>
                    )}
                    <button onClick={() => toggleArchive(inv)} data-testid={`invoice-archive-${inv.id}`}
                      className="h-10 px-3 rounded-lg border border-slate-300 text-slate-600 hover:border-slate-500 transition-colors text-xs font-bold inline-flex items-center gap-1.5 bg-white">
                      {inv.archived ? <><ArchiveRestore className="h-3.5 w-3.5" /> Restore</> : <><Archive className="h-3.5 w-3.5" /> Archive</>}
                    </button>
                    <button onClick={() => remove(inv)} data-testid={`invoice-delete-${inv.id}`}
                      className="h-10 px-3 rounded-lg border border-red-200 text-[#D5281B] hover:bg-red-50 transition-colors text-xs font-bold inline-flex items-center gap-1.5 bg-white">
                      <Trash2 className="h-3.5 w-3.5" /> Delete
                    </button>
                  </div>
                </div>
              );
            })}
                </div>
                )}
              </div>
            ))}
          </div>
        )}
      </main>

      <Dialog open={dialog} onOpenChange={setDialog}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto" data-testid="invoice-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading text-xl text-[#0F2547]">
              {editing ? `Edit ${editing.invoice_no}` : form.kind === "quote" ? "New quote" : "New invoice"}
            </DialogTitle>
          </DialogHeader>
          <form onSubmit={save} className="space-y-3 mt-1">
            <div>
              <label className="block text-xs font-semibold text-slate-600 mb-1">Client *</label>
              <select required value={form.client_id} data-testid="invoice-client-select"
                onChange={(e) => pickClient(e.target.value)}
                className={`${inputCls} bg-white`}>
                <option value="">Pick a client…</option>
                {clients.filter((c) => !c.archived).map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
              </select>
              {selectedClient && !selectedClient.contacts.length && !selectedClient.email && (
                <p className="text-[11px] text-[#ED8B00] font-semibold mt-1">This client has no email — add one in Client contacts or the {form.kind} can't be emailed.</p>
              )}
            </div>
            {selectedClient && selectedClient.contacts.length > 0 && (
              <div>
                <label className="block text-xs font-semibold text-slate-600 mb-1">Who at {selectedClient.name} should receive it?</label>
                <select value={form.contact_id} data-testid="invoice-contact-select"
                  onChange={(e) => setForm((f) => ({ ...f, contact_id: e.target.value }))}
                  className={`${inputCls} bg-white`}>
                  {selectedClient.email && <option value="">{`Company email — ${selectedClient.email}`}</option>}
                  {selectedClient.contacts.map((ct) => (
                    <option key={ct.contact_id} value={ct.contact_id}>{ct.name}{ct.email ? ` (${ct.email})` : ""}</option>
                  ))}
                </select>
              </div>
            )}
            <div>
              <label className="block text-xs font-semibold text-slate-600 mb-1">Calendar events * — search &amp; add one or more from the same client (each becomes its own editable line, or just type your own line below)</label>
              {editing ? (
                <p className="text-sm font-semibold text-[#0F2547] bg-[#F4F7FA] rounded-lg px-3 py-3">{editing.event_name}{editing.event_date && !editing.event_name.includes("+") ? ` — ${fmtDate(editing.event_date)}` : ""}</p>
              ) : (
                <div>
                  {form.event_ids.length > 0 && (
                    <div className="flex flex-wrap gap-1.5 mb-2">
                      {form.event_ids.map((id) => {
                        const ev = events.find((e) => e.id === id);
                        return (
                          <span key={id} data-testid={`invoice-event-chip-${id}`}
                            className="inline-flex items-center gap-1.5 bg-[#005EB8]/10 text-[#0F2547] text-xs font-semibold rounded-full pl-3 pr-1.5 py-1">
                            {ev?.name || "Event"}{ev?.event_date ? ` — ${fmtDate(ev.event_date)}` : ""}
                            <button type="button" onClick={() => toggleEvent(id)} data-testid={`invoice-event-remove-${id}`}
                              className="h-5 w-5 grid place-items-center rounded-full hover:bg-white/70 transition-colors">
                              <XCircle className="h-3.5 w-3.5" />
                            </button>
                          </span>
                        );
                      })}
                    </div>
                  )}
                  <div className="relative">
                    <input value={eventSearch} placeholder="Search events by name…" data-testid="invoice-event-search"
                      onChange={(e) => { setEventSearch(e.target.value); setEventPickerOpen(true); }}
                      onFocus={() => setEventPickerOpen(true)}
                      onBlur={() => setTimeout(() => setEventPickerOpen(false), 150)}
                      className={`${inputCls} bg-white`} />
                    {eventPickerOpen && (
                      <div className="absolute z-10 inset-x-0 top-full mt-1 rounded-lg border border-slate-300 bg-white shadow-lg max-h-44 overflow-y-auto divide-y divide-slate-100" data-testid="invoice-event-select">
                        {eventMatches.map((ev) => (
                          <button type="button" key={ev.id} data-testid={`invoice-event-option-${ev.id}`}
                            onMouseDown={(e) => { e.preventDefault(); toggleEvent(ev.id); setEventSearch(""); setEventPickerOpen(false); }}
                            className="w-full text-left px-3 py-2.5 text-sm text-slate-700 hover:bg-slate-50">
                            {ev.name}{ev.event_date ? ` — ${fmtDate(ev.event_date)}` : ""}
                          </button>
                        ))}
                        {eventMatches.length === 0 && <p className="px-3 py-3 text-sm text-slate-400">{form.client_id ? "No matching events for this client without an invoice" : "No matching upcoming events without an invoice"}</p>}
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>
            <div>
              <label className="block text-xs font-semibold text-slate-600 mb-1">Payment due date *</label>
              <input type="date" required value={form.due_date}
                onChange={(e) => setForm((f) => ({ ...f, due_date: e.target.value }))}
                data-testid="invoice-due-date" className={inputCls} />
            </div>

            <div>
              <label className="block text-xs font-semibold text-slate-600 mb-1">Lines *</label>
              <div className="space-y-2">
                {form.items.map((item, idx) => (
                  <div key={item._k || idx} className="grid grid-cols-[minmax(0,1fr)_52px_78px_34px] gap-1.5 items-center">
                    <input placeholder="Description (e.g. Event medical cover)" value={item.description}
                      onChange={(e) => setItem(idx, "description", e.target.value)}
                      data-testid={`invoice-item-desc-${idx}`} className="w-full min-w-0 h-11 rounded-lg border border-slate-300 px-3 text-sm focus:outline-none focus:ring-2 focus:ring-[#005EB8]" />
                    <input type="number" min="1" step="1" value={item.qty}
                      onChange={(e) => setItem(idx, "qty", e.target.value)}
                      data-testid={`invoice-item-qty-${idx}`} className="w-full min-w-0 h-11 rounded-lg border border-slate-300 px-2 text-sm text-center focus:outline-none focus:ring-2 focus:ring-[#005EB8]" />
                    <input type="number" min="0" step="0.01" placeholder="£ each" value={item.unit_price}
                      onChange={(e) => setItem(idx, "unit_price", e.target.value)}
                      data-testid={`invoice-item-price-${idx}`} className="w-full min-w-0 h-11 rounded-lg border border-slate-300 px-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#005EB8]" />
                    <button type="button" onClick={() => setForm((f) => ({ ...f, items: f.items.filter((_, i) => i !== idx) }))}
                      disabled={form.items.length === 1} data-testid={`invoice-item-remove-${idx}`}
                      className="h-11 w-9 grid place-items-center rounded-lg border border-slate-300 text-slate-400 hover:text-[#D5281B] hover:border-red-300 disabled:opacity-40 transition-colors">
                      <Trash2 className="h-3.5 w-3.5" />
                    </button>
                  </div>
                ))}
              </div>
              <button type="button" onClick={() => setForm((f) => ({ ...f, items: [...f.items, newItem()] }))}
                data-testid="invoice-item-add"
                className="mt-2 h-10 px-3 rounded-lg border-2 border-dashed border-slate-300 text-slate-500 hover:border-[#005EB8] hover:text-[#005EB8] transition-colors text-xs font-bold inline-flex items-center gap-1.5">
                <Plus className="h-3.5 w-3.5" /> Add line
              </button>
            </div>

            <textarea rows={2} placeholder="Notes for the client (payment details, references…)" value={form.notes}
              onChange={(e) => setForm((f) => ({ ...f, notes: e.target.value }))}
              data-testid="invoice-notes"
              className="w-full rounded-lg border border-slate-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#005EB8]" />

            {!form.discount_on ? (
              <button type="button" onClick={() => setForm((f) => ({ ...f, discount_on: true }))}
                data-testid="invoice-discount-apply"
                className="text-xs font-bold text-[#005EB8] hover:underline text-left">+ Apply discount</button>
            ) : (
              <div className="rounded-xl border border-slate-200 p-3 space-y-1" data-testid="invoice-discount-section">
                <span className="text-[11px] font-semibold text-slate-500 block">Discount amount</span>
                <div className="grid grid-cols-[1fr_auto_auto] gap-2">
                  <input type="number" min="0" step="0.01" value={form.discount_value}
                    onChange={(e) => setForm((f) => ({ ...f, discount_value: e.target.value }))}
                    placeholder={form.discount_type === "percent" ? "e.g. 10" : "e.g. 100"}
                    data-testid="invoice-discount-value" className={inputCls} />
                  <select value={form.discount_type} onChange={(e) => setForm((f) => ({ ...f, discount_type: e.target.value }))}
                    data-testid="invoice-discount-type" className={`${inputCls} w-20 shrink-0`}>
                    <option value="percent">%</option>
                    <option value="fixed">£</option>
                  </select>
                  <button type="button" onClick={() => setForm((f) => ({ ...f, discount_on: false, discount_value: "" }))}
                    data-testid="invoice-discount-remove"
                    className="h-11 px-3 rounded-lg border border-red-200 text-[#D5281B] hover:bg-red-50 transition-colors text-xs font-bold shrink-0">Remove</button>
                </div>
              </div>
            )}

            <div className="rounded-xl border border-slate-200 p-3 space-y-2">
              <p className="text-[11px] font-bold text-slate-500 uppercase tracking-wide">Paid into — account on this {form.kind}</p>
              <select value={form.bank_account_id} onChange={(e) => setForm((f) => ({ ...f, bank_account_id: e.target.value }))}
                data-testid="invoice-bank-account-select" className={`${inputCls} bg-white`}>
                {bankAccounts.map((a) => <option key={a.id} value={a.id}>{`${a.label} — ${a.bank_name} · ${a.sort_code}`}</option>)}
              </select>
              <p className="text-[11px] text-slate-400">Defaults to the client's account — change it here for this one only</p>
            </div>

            <div className="flex items-center justify-between rounded-xl bg-[#F4F7FA] border border-slate-200 px-4 py-3">
              <span className="text-sm font-semibold text-slate-600">Total</span>
              {discountAmt > 0 && (
                <span className="text-xs font-bold text-[#D5281B] block mb-0.5" data-testid="invoice-form-discount">
                  £{subtotal.toFixed(2)} − £{discountAmt.toFixed(2)} discount
                </span>
              )}
              <span className="text-xl font-black text-[#0F2547]" data-testid="invoice-form-total">£{total.toFixed(2)}</span>
            </div>
            <button type="submit" disabled={saving} data-testid="invoice-save"
              className="w-full h-12 rounded-lg bg-[#005EB8] hover:bg-[#004a93] disabled:opacity-60 transition-colors text-white text-sm font-bold">
              {saving ? "Saving…" : editing ? "Save changes" : form.kind === "quote" ? "Create quote" : "Create invoice"}
            </button>
          </form>
        </DialogContent>
      </Dialog>

      <Dialog open={bankDialog} onOpenChange={setBankDialog}>
        <DialogContent className="max-w-md max-h-[90vh] overflow-y-auto" data-testid="bank-accounts-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading text-xl text-[#0F2547]">Your bank accounts</DialogTitle>
          </DialogHeader>
          <p className="text-xs text-slate-500">These are the accounts clients can pay you into. Set them once, then just pick one per client or per invoice.</p>
          <div className="space-y-3 mt-1">
            {bankList.map((a, i) => (
              <div key={a.id || i} className="rounded-xl border border-slate-200 p-3 space-y-2" data-testid={`bank-account-${i}`}>
                <div className="flex items-center gap-2">
                  <input placeholder="Label (e.g. Main account)" value={a.label}
                    onChange={(e) => setBankList((l) => l.map((x, xi) => xi === i ? { ...x, label: e.target.value } : x))}
                    data-testid={`bank-label-${i}`} className={inputCls} />
                  {bankList.length > 1 && (
                    <button type="button" onClick={() => setBankList((l) => l.filter((_, xi) => xi !== i))} data-testid={`bank-remove-${i}`}
                      className="h-11 w-11 shrink-0 grid place-items-center rounded-lg border border-red-200 text-[#D5281B] hover:bg-red-50 transition-colors">
                      <Trash2 className="h-4 w-4" />
                    </button>
                  )}
                </div>
                <input placeholder="Bank name (e.g. Starling Bank)" value={a.bank_name}
                  onChange={(e) => setBankList((l) => l.map((x, xi) => xi === i ? { ...x, bank_name: e.target.value } : x))}
                  data-testid={`bank-name-${i}`} className={inputCls} />
                <input placeholder="Account name" value={a.account_name}
                  onChange={(e) => setBankList((l) => l.map((x, xi) => xi === i ? { ...x, account_name: e.target.value } : x))}
                  data-testid={`bank-acct-name-${i}`} className={inputCls} />
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                  <input placeholder="Account number" value={a.account_number}
                    onChange={(e) => setBankList((l) => l.map((x, xi) => xi === i ? { ...x, account_number: e.target.value } : x))}
                    data-testid={`bank-number-${i}`} className={inputCls} />
                  <input placeholder="Sort code" value={a.sort_code}
                    onChange={(e) => setBankList((l) => l.map((x, xi) => xi === i ? { ...x, sort_code: e.target.value } : x))}
                    data-testid={`bank-sort-${i}`} className={inputCls} />
                </div>
              </div>
            ))}
          </div>
          {bankList.length < 5 && (
            <button type="button" onClick={() => setBankList((l) => [...l, { id: "", label: "", bank_name: "", account_name: "", account_number: "", sort_code: "" }])}
              data-testid="bank-add"
              className="w-full h-11 mt-3 rounded-lg border-2 border-dashed border-slate-300 text-slate-500 hover:border-[#005EB8] hover:text-[#005EB8] transition-colors text-xs font-bold">
              + Add another account
            </button>
          )}
          <button onClick={saveBanks} disabled={saving} data-testid="bank-save"
            className="w-full h-12 mt-3 rounded-lg bg-[#005EB8] hover:bg-[#004a93] disabled:opacity-60 transition-colors text-white text-sm font-bold">
            {saving ? "Saving…" : "Save accounts"}
          </button>
        </DialogContent>
      </Dialog>

      <Dialog open={!!depositDialog} onOpenChange={() => setDepositDialog(null)}>
        <DialogContent className="max-w-sm" data-testid="deposit-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading text-xl text-[#0F2547]">Deposit paid — {depositDialog?.invoice_no}</DialogTitle>
          </DialogHeader>
          <p className="text-xs text-slate-500">Total £{depositDialog?.total.toFixed(2)}
            {(depositDialog?.deposit_total || 0) > 0 && ` · deposits so far £${depositDialog.deposit_total.toFixed(2)} · left £${depositDialog.balance_due.toFixed(2)}`}</p>
          <label className="block">
            <span className="text-xs font-semibold text-slate-600 block mb-1">Deposit amount (£)</span>
            <input type="number" min="0" step="0.01" value={depositAmt} onChange={(e) => setDepositAmt(e.target.value)}
              data-testid="deposit-amount" className={inputCls} placeholder="0.00" />
          </label>
          <input value={depositNote} onChange={(e) => setDepositNote(e.target.value)} placeholder="Note (optional — e.g. bank transfer ref)"
            data-testid="deposit-note" className={inputCls} />
          <button onClick={confirmDeposit} disabled={saving} data-testid="deposit-confirm"
            className="w-full h-12 rounded-lg bg-[#007F3B] hover:bg-[#006631] disabled:opacity-60 transition-colors text-white text-sm font-bold">
            {saving ? "Saving…" : "Add deposit"}
          </button>
        </DialogContent>
      </Dialog>

      <Dialog open={!!paidDialog} onOpenChange={() => setPaidDialog(null)}>
        <DialogContent className="max-w-sm" data-testid="mark-paid-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading text-xl text-[#0F2547]">Mark {paidDialog?.invoice_no} as paid</DialogTitle>
          </DialogHeader>
          <p className="text-xs text-slate-500">Pick when the payment was actually received — you can backdate it.</p>
          <input type="datetime-local" value={paidAt} onChange={(e) => setPaidAt(e.target.value)}
            data-testid="mark-paid-date" className={inputCls} />
          <button onClick={confirmPaid} data-testid="mark-paid-confirm"
            className="w-full h-12 rounded-lg bg-[#007F3B] hover:bg-[#006631] transition-colors text-white text-sm font-bold">
            Confirm paid — invoice will be archived
          </button>
        </DialogContent>
      </Dialog>

      <Dialog open={!!sendDialog} onOpenChange={() => setSendDialog(null)}>
        <DialogContent className="max-w-sm" data-testid="invoice-send-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading text-xl text-[#0F2547]">Send {sendDialog?.invoice_no}</DialogTitle>
          </DialogHeader>
          {(() => {
            const cl = clients.find((c) => c.id === sendDialog?.client_id);
            const others = (cl?.contacts || []).filter((ct) => ct.email && ct.email !== sendDialog?.contact_email);
            const otherIds = others.map((ct) => ct.contact_id);
            const allPicked = otherIds.length > 0 && otherIds.every((id) => sendPick.contact_ids.includes(id));
            return (
              <div className="space-y-3">
                <div>
                  <p className="text-xs font-semibold text-slate-600 mb-1">Always goes to</p>
                  <p className="text-sm font-bold text-[#0F2547] bg-slate-50 border border-slate-200 rounded-lg px-3 py-2" data-testid="send-main-recipient">
                    {sendDialog?.contact_name ? `${sendDialog.contact_name} — ` : ""}{sendDialog?.contact_email || "no main email set"}
                  </p>
                </div>
                {others.length > 0 && (
                  <div>
                    <div className="flex items-center justify-between mb-1">
                      <p className="text-xs font-semibold text-slate-600">Also send to (e.g. the treasurer)</p>
                      <button type="button" data-testid="send-select-all"
                        onClick={() => setSendPick((f) => ({ ...f, contact_ids: allPicked ? [] : otherIds }))}
                        className="text-[11px] font-bold text-[#005EB8] hover:underline">
                        {allPicked ? "Clear all" : "Select all"}
                      </button>
                    </div>
                    <div className="space-y-1.5" data-testid="send-contact-list">
                      {others.map((ct) => (
                        <label key={ct.contact_id} className="flex items-center gap-2.5 rounded-lg border border-slate-200 px-3 py-2 cursor-pointer hover:border-[#005EB8] transition-colors" data-testid={`send-contact-${ct.contact_id}`}>
                          <input type="checkbox" checked={sendPick.contact_ids.includes(ct.contact_id)}
                            onChange={(e) => setSendPick((f) => ({ ...f, contact_ids: e.target.checked ? [...f.contact_ids, ct.contact_id] : f.contact_ids.filter((id) => id !== ct.contact_id) }))}
                            className="h-4 w-4 accent-[#005EB8]" />
                          <span className="text-xs font-semibold text-[#0F2547]">{ct.name}{ct.role ? ` (${ct.role})` : ""}</span>
                          <span className="text-[11px] text-slate-400 truncate ml-auto">{ct.email}</span>
                        </label>
                      ))}
                    </div>
                  </div>
                )}
                <label className="block">
                  <span className="text-xs font-semibold text-slate-600 block mb-1">Extra email addresses (optional, comma separated)</span>
                  <input value={sendPick.extra} onChange={(e) => setSendPick((f) => ({ ...f, extra: e.target.value }))}
                    placeholder="treasurer@example.org" data-testid="send-extra-emails" className={inputCls} />
                </label>
                <button onClick={confirmSend} disabled={saving || (!sendDialog?.contact_email && sendPick.contact_ids.length === 0 && !sendPick.extra.includes("@"))}
                  data-testid="send-confirm"
                  className="w-full h-12 rounded-lg bg-[#005EB8] hover:bg-[#004a93] transition-colors text-white text-sm font-bold inline-flex items-center justify-center gap-2 disabled:opacity-50">
                  <Send className="h-4 w-4" /> {saving ? "Sending…" : `Send to ${1 + sendPick.contact_ids.length + sendPick.extra.split(/[,\s;]+/).filter((e) => e.includes("@")).length} recipient${sendPick.contact_ids.length + sendPick.extra.split(/[,\s;]+/).filter((e) => e.includes("@")).length ? "s" : ""}`}
                </button>
              </div>
            );
          })()}
        </DialogContent>
      </Dialog>

      <Dialog open={!!chaseDialog} onOpenChange={() => setChaseDialog(null)}>
        <DialogContent className="max-w-sm" data-testid="chase-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading text-xl text-[#0F2547]">Chase {chaseDialog?.invoice_no}</DialogTitle>
          </DialogHeader>
          <p className="text-xs text-slate-500" data-testid="chase-dialog-hint">
            Sending to <strong>{chaseDialog?.contact_email}</strong> — payment due {chaseDialog?.due_date}.
            Add your own words below (optional), then pick how to send it.
          </p>
          <textarea rows={4} value={chaseMsg} onChange={(e) => setChaseMsg(e.target.value)}
            placeholder="Optional message to include in the email…"
            data-testid="chase-message" className="w-full rounded-lg border border-slate-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#005EB8]" />
          <button onClick={() => sendChase("chaser")} disabled={saving || !chaseMsg.trim()} data-testid="chase-send-with-message"
            className="w-full h-12 rounded-lg bg-[#ED8B00] hover:bg-[#c97600] transition-colors text-white text-sm font-bold disabled:opacity-50">
            Send chaser with my message
          </button>
          <button onClick={() => sendChase("normal")} disabled={saving || !chaseMsg.trim()} data-testid="chase-send-normal-with-message"
            className="w-full h-12 rounded-lg border-2 border-[#005EB8] text-[#005EB8] hover:bg-sky-50 transition-colors text-sm font-bold bg-white disabled:opacity-50">
            Send as normal invoice with my message
          </button>
          <button onClick={() => sendChase("chaser")} disabled={saving} data-testid="chase-send-plain"
            className="w-full h-12 rounded-lg border border-slate-300 text-slate-600 hover:border-[#ED8B00] hover:text-[#ED8B00] transition-colors text-sm font-bold bg-white disabled:opacity-50">
            Just send the standard chaser
          </button>
        </DialogContent>
      </Dialog>

      <Dialog open={uploadOpen} onOpenChange={setUploadOpen}>
        <DialogContent className="max-w-md" data-testid="inv-upload-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading text-xl text-[#0F2547]">Upload a previous invoice</DialogTitle>
          </DialogHeader>
          <p className="text-xs text-slate-500">Store older invoices here so everything's in one place. The file is kept safe and can be downloaded any time.</p>
          <label className="block">
            <span className="text-xs font-semibold text-slate-600 block mb-1">Invoice file (PDF, Word or image) *</span>
            <input type="file" accept=".pdf,.doc,.docx,.png,.jpg,.jpeg" data-testid="inv-upload-file"
              onChange={(e) => setUploadFile(e.target.files?.[0] || null)}
              className="w-full text-sm text-slate-600 file:mr-3 file:h-10 file:px-4 file:rounded-lg file:border-0 file:bg-slate-100 file:text-sm file:font-bold file:text-[#0F2547] hover:file:bg-slate-200" />
          </label>
          <label className="block">
            <span className="text-xs font-semibold text-slate-600 block mb-1">Client</span>
            <select className="w-full h-11 rounded-lg border border-slate-300 px-3 text-sm bg-white" value={uploadForm.client_id}
              onChange={(e) => setUploadForm((f) => ({ ...f, client_id: e.target.value }))} data-testid="inv-upload-client">
              <option value="">— Not in the system / type below —</option>
              {clients.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
            </select>
          </label>
          {!uploadForm.client_id && (
            <label className="block">
              <span className="text-xs font-semibold text-slate-600 block mb-1">Client name *</span>
              <input className="w-full h-11 rounded-lg border border-slate-300 px-3 text-sm" value={uploadForm.client_name}
                onChange={(e) => setUploadForm((f) => ({ ...f, client_name: e.target.value }))} placeholder="e.g. Greenfield Festival" data-testid="inv-upload-clientname" />
            </label>
          )}
          <div className="grid grid-cols-2 gap-3">
            <label className="block">
              <span className="text-xs font-semibold text-slate-600 block mb-1">Invoice no. (optional)</span>
              <input className="w-full h-11 rounded-lg border border-slate-300 px-3 text-sm" value={uploadForm.invoice_no}
                onChange={(e) => setUploadForm((f) => ({ ...f, invoice_no: e.target.value }))} placeholder="Auto (WEMS-…)" data-testid="inv-upload-no" />
            </label>
            <label className="block">
              <span className="text-xs font-semibold text-slate-600 block mb-1">Total (£)</span>
              <input type="number" min="0" step="0.01" className="w-full h-11 rounded-lg border border-slate-300 px-3 text-sm" value={uploadForm.total}
                onChange={(e) => setUploadForm((f) => ({ ...f, total: e.target.value }))} placeholder="0.00" data-testid="inv-upload-total" />
            </label>
          </div>
          <label className="block">
            <span className="text-xs font-semibold text-slate-600 block mb-1">Event / what it was for (optional)</span>
            <input className="w-full h-11 rounded-lg border border-slate-300 px-3 text-sm" value={uploadForm.event_name}
              onChange={(e) => setUploadForm((f) => ({ ...f, event_name: e.target.value }))} placeholder="e.g. Summer Festival 2025" data-testid="inv-upload-event" />
          </label>
          <div className="grid grid-cols-2 gap-3">
            <label className="block">
              <span className="text-xs font-semibold text-slate-600 block mb-1">Due date (optional)</span>
              <input type="date" className="w-full h-11 rounded-lg border border-slate-300 px-3 text-sm" value={uploadForm.due_date}
                onChange={(e) => setUploadForm((f) => ({ ...f, due_date: e.target.value }))} data-testid="inv-upload-due" />
            </label>
            <label className="block">
              <span className="text-xs font-semibold text-slate-600 block mb-1">Status</span>
              <select className="w-full h-11 rounded-lg border border-slate-300 px-3 text-sm bg-white" value={uploadForm.status}
                onChange={(e) => setUploadForm((f) => ({ ...f, status: e.target.value }))} data-testid="inv-upload-status">
                <option value="unpaid">Unpaid — still chasing</option>
                <option value="paid">Paid — file it away</option>
              </select>
            </label>
          </div>
          <label className="block">
            <span className="text-xs font-semibold text-slate-600 block mb-1">Notes (optional)</span>
            <textarea rows={2} className="w-full rounded-lg border border-slate-300 px-3 py-2.5 text-sm" value={uploadForm.notes}
              onChange={(e) => setUploadForm((f) => ({ ...f, notes: e.target.value }))} data-testid="inv-upload-notes" />
          </label>
          <button onClick={uploadInvoice} disabled={uploading} data-testid="inv-upload-confirm"
            className="w-full h-12 rounded-lg bg-[#005EB8] hover:bg-[#004a93] transition-colors text-white text-sm font-bold disabled:opacity-50">
            {uploading ? "Uploading…" : "Upload invoice"}
          </button>
        </DialogContent>
      </Dialog>
    </div>
  );
}
